(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function registerCleanup(root, callback) {
    if (!root || typeof callback !== 'function') return;
    root._mtCleanup = root._mtCleanup || [];
    root._mtCleanup.push(callback);
  }

  function cleanup(root) {
    const targets = [root, ...(root?.querySelectorAll?.('[data-mt-section], [data-mt-header], [data-mt-gallery]') || [])];
    targets.forEach((target) => {
      (target?._mtCleanup || []).splice(0).forEach((callback) => {
        try { callback(); } catch (_) { /* Section removal must never block the editor. */ }
      });
    });
  }

  function trapFocus(event, container) {
    if (event.key !== 'Tab' || !container) return;
    const focusable = [...container.querySelectorAll(focusableSelector)].filter((element) => {
      return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0;
    });
    if (!focusable.length) {
      event.preventDefault();
      container.focus?.();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === container || !container.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === container || !container.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  function initMenu(root = document) {
    const header = root.querySelector?.('[data-mt-header]') || document.querySelector('[data-mt-header]');
    if (!header || header.dataset.mtReady === 'true') return;
    header.dataset.mtReady = 'true';

    const menu = document.querySelector('[data-mt-menu]');
    const openButton = header.querySelector('[data-mt-menu-open]');
    const closeButton = menu?.querySelector('[data-mt-menu-close]');
    let lastFocus = null;
    let focusTimer = null;

    const closeMenu = () => {
      if (!menu) return;
      if (focusTimer) window.clearTimeout(focusTimer);
      focusTimer = null;
      menu.setAttribute('aria-hidden', 'true');
      menu.setAttribute('inert', '');
      openButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('mt-menu-open');
      document.body.style.overflow = '';
      lastFocus?.focus?.();
    };

    const openMenu = () => {
      if (!menu) return;
      lastFocus = document.activeElement;
      menu.setAttribute('aria-hidden', 'false');
      menu.removeAttribute('inert');
      openButton?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('mt-menu-open');
      document.body.style.overflow = 'hidden';
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => closeButton?.focus());
      });
      focusTimer = window.setTimeout(() => {
        if (menu.getAttribute('aria-hidden') === 'false') closeButton?.focus();
      }, 320);
    };

    openButton?.addEventListener('click', openMenu);
    closeButton?.addEventListener('click', closeMenu);
    menu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
    const onKeydown = (event) => {
      if (menu?.getAttribute('aria-hidden') !== 'false') return;
      if (event.key === 'Escape') closeMenu();
      else trapFocus(event, menu);
    };
    document.addEventListener('keydown', onKeydown);
    registerCleanup(header, () => {
      document.removeEventListener('keydown', onKeydown);
      if (menu?.getAttribute('aria-hidden') === 'false') closeMenu();
    });
  }

  function initGallery(section) {
    const gallery = section.querySelector('[data-mt-gallery]');
    if (!gallery || gallery.dataset.mtReady === 'true') return;
    gallery.dataset.mtReady = 'true';

    const viewport = gallery.querySelector('[data-mt-gallery-viewport]');
    const track = gallery.querySelector('[data-mt-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-mt-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-mt-thumb]')];
    const dots = [...gallery.querySelectorAll('[data-mt-dot]')];
    const counter = gallery.querySelector('[data-mt-counter]');
    const prev = gallery.querySelector('[data-mt-prev]');
    const next = gallery.querySelector('[data-mt-next]');
    const autoplay = gallery.dataset.autoplay === 'true';
    const intervalMs = Math.max(3000, Number(gallery.dataset.interval || 5000));
    let index = 0;
    let timer = null;
    let startX = 0;
    let currentX = 0;
    let dragging = false;

    const update = (newIndex, userInitiated = false) => {
      if (!slides.length) return;
      index = (newIndex + slides.length) % slides.length;
      track.style.transform = `translate3d(${-index * 100}%, 0, 0)`;
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.toggleAttribute('inert', !isActive);
        if (!isActive) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      thumbs.forEach((thumb, thumbIndex) => {
        thumb.setAttribute('aria-current', thumbIndex === index ? 'true' : 'false');
      });
      dots.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
      thumbs[index]?.scrollIntoView?.({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      if (userInitiated) restart();
    };

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      if (autoplay && slides.length > 1 && !reducedMotion.matches) {
        timer = window.setInterval(() => update(index + 1), intervalMs);
      }
    };

    const restart = () => {
      stop();
      start();
    };

    prev?.addEventListener('click', () => update(index - 1, true));
    next?.addEventListener('click', () => update(index + 1, true));
    thumbs.forEach((thumb, thumbIndex) => thumb.addEventListener('click', () => update(thumbIndex, true)));
    dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => update(dotIndex, true)));

    viewport?.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('button, a, input, select, textarea, [role="button"]')) return;
      dragging = true;
      startX = event.clientX;
      currentX = startX;
      gallery.classList.add('is-dragging');
      viewport.setPointerCapture?.(event.pointerId);
      stop();
    });

    viewport?.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      currentX = event.clientX;
      const width = viewport.clientWidth || 1;
      const delta = currentX - startX;
      track.style.transform = `translate3d(calc(${-index * 100}% + ${delta}px), 0, 0)`;
      if (Math.abs(delta) > width * .04) event.preventDefault();
    });

    const finishDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      gallery.classList.remove('is-dragging');
      const delta = currentX - startX;
      if (Math.abs(delta) > 44) update(index + (delta < 0 ? 1 : -1), true);
      else update(index, true);
      viewport.releasePointerCapture?.(event.pointerId);
    };

    viewport?.addEventListener('pointerup', finishDrag);
    viewport?.addEventListener('pointercancel', finishDrag);
    viewport?.addEventListener('mouseenter', stop);
    viewport?.addEventListener('mouseleave', start);
    gallery.addEventListener('focusin', stop);
    gallery.addEventListener('focusout', start);
    reducedMotion.addEventListener?.('change', restart);
    registerCleanup(gallery, () => {
      stop();
      reducedMotion.removeEventListener?.('change', restart);
    });
    update(0);
    start();
  }

  function initBundles(section) {
    if (!section.matches?.('[data-mt-main-product]') || section.dataset.mtBundlesReady === 'true') return;
    section.dataset.mtBundlesReady = 'true';
    const quantityInput = section.querySelector('[data-mt-quantity-input]');
    const radios = [...section.querySelectorAll('[data-mt-bundle-input]')];
    const priceTargets = section.querySelectorAll('[data-mt-atc-price]');
    const progressLabel = section.querySelector('[data-mt-bundle-progress-label]');
    const progressBar = section.querySelector('[data-mt-bundle-progress-bar]');
    const sheetButtons = [...section.querySelectorAll('[data-mt-sheet-quantity]')];
    if (!quantityInput || !radios.length) return;

    const apply = (radio) => {
      const quantity = Number(radio.value);
      quantityInput.value = radio.value;
      priceTargets.forEach((target) => {
        target.textContent = radio.dataset.totalMoney || '';
      });
      if (progressLabel) progressLabel.textContent = `${quantity} ${quantity === 1 ? 'Karte' : 'Karten'}`;
      const maxQuantity = Number(progressBar?.closest('[data-max-quantity]')?.dataset.maxQuantity || Math.max(...radios.map((item) => Number(item.value)), 1));
      if (progressBar) progressBar.style.width = `${Math.max(0, Math.min(100, quantity / maxQuantity * 100))}%`;
      sheetButtons.forEach((button) => button.setAttribute('aria-pressed', button.dataset.mtSheetQuantity === radio.value ? 'true' : 'false'));
      section.dispatchEvent(new CustomEvent('montrea:quantity-change', {
        bubbles: true,
        detail: { quantity }
      }));
    };

    radios.forEach((radio) => radio.addEventListener('change', () => apply(radio)));
    sheetButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const radio = radios.find((item) => item.value === button.dataset.mtSheetQuantity);
        if (!radio) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    apply(radios.find((radio) => radio.checked) || radios[0]);

    section.querySelectorAll('[data-mt-submit]').forEach((button) => {
      button.addEventListener('click', () => {
        const mainButton = section.querySelector('[data-mt-main-submit]');
        if (!mainButton || mainButton.disabled) return;
        mainButton.click();
      });
    });
  }

  function initVariants(section) {
    if (!section.matches?.('[data-mt-main-product]') || section.dataset.mtVariantsReady === 'true') return;
    section.dataset.mtVariantsReady = 'true';
    const select = section.querySelector('[data-mt-variant-select]');
    const json = section.querySelector('[data-mt-product-json]');
    if (!select || !json) return;

    let variants = [];
    try {
      variants = JSON.parse(json.textContent || '[]');
    } catch (_) {
      return;
    }

    const currency = section.dataset.currency || 'EUR';
    const locale = section.dataset.locale || document.documentElement.lang || 'de';
    const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
    const money = (minorUnits) => formatter.format(Number(minorUnits || 0) / 100);
    const variantId = section.querySelector('[data-mt-variant-id]');
    const unitPrice = section.querySelector('[data-mt-unit-price]');
    const comparePrice = section.querySelector('[data-mt-compare-price]');
    const submit = section.querySelector('[data-mt-main-submit]');
    const submitLabel = submit?.querySelector('.main-atc__label__text');
    const submitMobileLabel = submit?.querySelector('.main-atc__label-mobile');
    const submitSeparator = submit?.querySelector('.main-atc__separator');
    const submitPrice = submit?.querySelector('.main-atc__price');
    const addLabel = section.dataset.addLabel || submitLabel?.textContent || 'In den Warenkorb';
    const addMobileLabel = section.dataset.addMobileLabel || submitMobileLabel?.textContent || addLabel;
    const stickyLabel = section.dataset.stickyLabel || addMobileLabel;
    const soldOutLabel = section.dataset.soldOutLabel || 'Ausverkauft';
    const stock = section.querySelector('[data-mt-stock]');
    const stockText = section.querySelector('[data-mt-stock-text]');

    const update = () => {
      const variant = variants.find((item) => String(item.id) === select.value);
      if (!variant) return;
      if (variantId) variantId.value = variant.id;
      if (unitPrice) unitPrice.textContent = money(variant.price);
      if (comparePrice) {
        const showCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        comparePrice.textContent = showCompare ? money(variant.compare_at_price) : '';
        comparePrice.classList.toggle('hidden', !showCompare);
      }

      section.querySelectorAll('[data-mt-bundle-input]').forEach((radio) => {
        const total = Number(variant.price || 0) * Number(radio.value || 1);
        radio.dataset.totalMoney = money(total);
        radio.closest('.mt-bundle')?.querySelector('[data-mt-bundle-price]')?.replaceChildren(document.createTextNode(money(total)));
      });
      section.querySelectorAll('[data-mt-sheet-quantity]').forEach((button) => {
        const total = Number(variant.price || 0) * Number(button.dataset.mtSheetQuantity || 1);
        button.querySelector('[data-mt-sheet-price]')?.replaceChildren(document.createTextNode(money(total)));
      });

      const available = Boolean(variant.available);
      if (submit) {
        submit.disabled = !available;
        submit.dataset.unavailable = available ? 'false' : 'true';
      }
      if (submitLabel) submitLabel.textContent = available ? addLabel : soldOutLabel;
      if (submitMobileLabel) submitMobileLabel.textContent = available ? addMobileLabel : soldOutLabel;
      submitSeparator?.classList.toggle('hidden', !available);
      submitPrice?.classList.toggle('hidden', !available);
      section.querySelectorAll('[data-mt-sticky-open], .mt-bundle-sheet__submit').forEach((button) => {
        button.disabled = !available;
        button.setAttribute('aria-disabled', available ? 'false' : 'true');
      });
      const stickyButton = section.querySelector('[data-mt-sticky-open]');
      if (stickyButton) stickyButton.textContent = available ? stickyLabel : soldOutLabel;
      const sheetSubmitLabel = section.querySelector('[data-mt-sheet-submit-label]');
      if (sheetSubmitLabel) sheetSubmitLabel.textContent = available ? addLabel : soldOutLabel;
      section.querySelector('.mt-bundle-sheet__price')?.classList.toggle('hidden', !available);
      stock?.classList.toggle('is-unavailable', !available);
      if (stockText) stockText.textContent = available ? (section.dataset.inStockLabel || 'Auf Lager') : soldOutLabel;
      const checked = section.querySelector('[data-mt-bundle-input]:checked') || section.querySelector('[data-mt-bundle-input]');
      checked?.dispatchEvent(new Event('change', { bubbles: true }));
    };

    select.addEventListener('change', update);
    update();
  }

  function initQuiz(section) {
    const quiz = section.querySelector('[data-mt-quiz]');
    if (!quiz || quiz.dataset.mtReady === 'true') return;
    quiz.dataset.mtReady = 'true';

    const steps = [...quiz.querySelectorAll('[data-mt-quiz-step]')];
    const stepLabel = quiz.querySelector('[data-mt-quiz-step-label]');
    const progress = quiz.querySelector('[data-mt-quiz-progress]');
    const resultTitle = quiz.querySelector('[data-mt-result-title]');
    const resultText = quiz.querySelector('[data-mt-result-text]');
    const resultButton = quiz.querySelector('[data-mt-result-button]');
    const dialog = section.querySelector('[data-mt-quiz-dialog]');
    const dialogPanel = dialog?.querySelector('[role="dialog"]');
    const stepPrefix = quiz.dataset.mtStepLabel?.trim() || '';
    const resultTitleTemplate = resultTitle?.textContent?.trim() || '';
    const resultTextTemplate = resultText?.innerHTML || '';
    const resultButtonTemplate = resultButton?.textContent?.trim() || '';
    let quizLastFocus = null;
    let current = 0;

    const closeQuiz = () => {
      if (!dialog) return;
      dialog.setAttribute('aria-hidden', 'true');
      dialog.setAttribute('inert', '');
      document.body.style.overflow = '';
      quizLastFocus?.focus?.();
    };

    const openQuiz = () => {
      if (!dialog) return;
      quizLastFocus = document.activeElement;
      dialog.setAttribute('aria-hidden', 'false');
      dialog.removeAttribute('inert');
      document.body.style.overflow = 'hidden';
      window.requestAnimationFrame(() => dialogPanel?.focus());
    };

    section.querySelectorAll('[data-mt-quiz-open]').forEach((button) => button.addEventListener('click', openQuiz));
    dialog?.querySelectorAll('[data-mt-quiz-close]').forEach((button) => button.addEventListener('click', closeQuiz));
    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeQuiz();
      else trapFocus(event, dialogPanel);
    });

    const canContinue = (step) => {
      const controls = [...step.querySelectorAll('input')];
      if (!controls.length) return true;
      return controls.some((control) => control.checked);
    };

    const syncButtons = () => {
      const step = steps[current];
      step?.querySelectorAll('[data-mt-next-step]').forEach((button) => {
        button.disabled = !canContinue(step);
      });
    };

    const show = (nextIndex) => {
      current = Math.max(0, Math.min(nextIndex, steps.length - 1));
      steps.forEach((step, index) => { step.hidden = index !== current; });
      const visibleNumber = current + 1;
      if (stepLabel) stepLabel.textContent = `${stepPrefix ? `${stepPrefix} ` : ''}${visibleNumber} / ${steps.length}`;
      if (progress) progress.style.setProperty('--mt-progress', `${visibleNumber / steps.length * 100}%`);
      progress?.parentElement?.setAttribute('aria-valuenow', String(visibleNumber));
      syncButtons();
      quiz.querySelector('[data-mt-quiz-step]:not([hidden]) h3')?.focus?.({ preventScroll: true });
    };

    const replaceResultTokens = (template, values) => String(template || '').replace(
      /(?:\{\{\s*|\[\[\s*)(quantity|selection)(?:\s*\}\}|\s*\]\])/gi,
      (_match, key) => values[key.toLowerCase()] || ''
    );

    const renderRichResult = (template, values) => {
      if (!resultText) return;
      resultText.innerHTML = template;
      const walker = document.createTreeWalker(resultText, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        node.nodeValue = replaceResultTokens(node.nodeValue, values);
        node = walker.nextNode();
      }
    };

    const buildResult = () => {
      const useCases = [...quiz.querySelectorAll('input[name="mt_use"]:checked')].map((input) => input.value);
      const reason = quiz.querySelector('input[name="mt_reason"]:checked')?.value;
      const preferredQuantity = useCases.length > 1 || reason === 'gift' ? '3' : '1';
      const productSection = document.querySelector('[data-mt-main-product]');
      const bundleRadios = [...(productSection?.querySelectorAll('[data-mt-bundle-input]') || [])];
      const recommendedRadio = bundleRadios.find((radio) => radio.value === preferredQuantity)
        || bundleRadios.find((radio) => radio.value === '1')
        || bundleRadios[0];
      const recommendedQuantity = recommendedRadio?.value || '1';
      const recommendationLabel = recommendedRadio
        ?.closest('.mt-bundle')
        ?.querySelector('.mt-bundle__title')
        ?.textContent
        ?.trim() || recommendedQuantity;
      const values = { quantity: recommendedQuantity, selection: recommendationLabel };

      if (resultButton) resultButton.dataset.quantity = recommendedQuantity;
      if (resultTitle) resultTitle.textContent = replaceResultTokens(resultTitleTemplate, values);
      renderRichResult(resultTextTemplate, values);
      if (resultButton) resultButton.textContent = replaceResultTokens(resultButtonTemplate, values);
    };

    quiz.querySelectorAll('.mt-quiz__option').forEach((option) => {
      const input = option.querySelector('input');
      option.addEventListener('click', (event) => {
        if (!input || event.target === input) return;
        event.preventDefault();
        if (input.type === 'checkbox') input.checked = !input.checked;
        else input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    quiz.addEventListener('change', (event) => {
      syncButtons();
      if (event.target.matches('input[type="radio"]')) {
        window.setTimeout(() => show(current + 1), reducedMotion.matches ? 0 : 180);
      }
    });
    quiz.querySelectorAll('[data-mt-next-step]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!canContinue(steps[current])) return;
        if (current === steps.length - 2) buildResult();
        show(current + 1);
      });
    });
    quiz.querySelectorAll('[data-mt-prev-step]').forEach((button) => {
      button.addEventListener('click', () => show(current - 1));
    });

    resultButton?.addEventListener('click', () => {
      const quantity = resultButton.dataset.quantity || '1';
      const productSection = document.querySelector('[data-mt-main-product]');
      const radio = productSection?.querySelector(`[data-mt-bundle-input][value="${quantity}"]`) || productSection?.querySelector('[data-mt-bundle-input]');
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      closeQuiz();
      document.querySelector('#montrea-kaufen')?.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    });

    show(0);
    registerCleanup(section, () => {
      if (dialog?.getAttribute('aria-hidden') === 'false') closeQuiz();
    });
  }

  function initQuizLinks() {
    if (document.documentElement.dataset.mtQuizLinksReady === 'true') return;
    document.documentElement.dataset.mtQuizLinksReady = 'true';
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-mt-quiz-link]');
      if (!link) return;
      const launcher = document.querySelector('[data-mt-quiz-open]');
      if (!launcher) return;
      event.preventDefault();
      launcher.click();
    });
  }

  function initFeatureTabs(section) {
    section.querySelectorAll('[data-mt-feature-tabs]').forEach((tabs) => {
      if (tabs.dataset.mtReady === 'true') return;
      tabs.dataset.mtReady = 'true';
      const buttons = [...tabs.querySelectorAll('[data-mt-feature-tab]')];
      const panels = [...tabs.querySelectorAll('[data-mt-feature-panel]')];

      const activate = (index, focus = false) => {
        buttons.forEach((button, buttonIndex) => {
          const active = buttonIndex === index;
          button.setAttribute('aria-selected', active ? 'true' : 'false');
          button.tabIndex = active ? 0 : -1;
          if (active && focus) button.focus();
        });
        panels.forEach((panel, panelIndex) => {
          const active = panelIndex === index;
          panel.hidden = !active;
          panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      };

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(index));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          activate((index + direction + buttons.length) % buttons.length, true);
        });
      });
      activate(0);
    });
  }

  function initFaq(section) {
    section.querySelectorAll('[data-mt-faq]').forEach((faq) => {
      const items = [...faq.querySelectorAll('[data-mt-faq-item]')];
      const filters = [...section.querySelectorAll('[data-mt-faq-filter]')];

      const resolvedCategory = (item) => {
        const question = item.querySelector('summary')?.textContent?.toLowerCase() || '';
        if (/rückgabe|widerruf|gebühr|hilfe|bestellung/.test(question)) return 'support';
        if (/kompatib|smartphone|geladen|laden/.test(question)) return 'compatibility';
        if (/app|ortung|funktioniert/.test(question)) return 'setup';
        return item.dataset.category || 'product';
      };

      items.forEach((item) => { item.dataset.mtFaqResolvedCategory = resolvedCategory(item); });

      items.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          items.forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });

      filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          const category = filter.dataset.mtFaqFilter;
          filters.forEach((button) => button.setAttribute('aria-selected', button === filter ? 'true' : 'false'));
          items.forEach((item) => {
            item.open = false;
            item.hidden = category !== 'all' && item.dataset.mtFaqResolvedCategory !== category;
          });
        });
      });
    });
  }

  function initBuyAccordions(section) {
    const groups = section.querySelectorAll('[data-mt-buy-accordions]');
    groups.forEach((group) => {
      group.querySelectorAll('details').forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          group.querySelectorAll('details[open]').forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });
    });
  }

  function initSticky(section) {
    const sticky = section.querySelector('[data-mt-sticky]');
    const mainButton = section.querySelector('[data-mt-main-submit]');
    const sheet = section.querySelector('[data-mt-bundle-sheet]');
    const openButton = sticky?.querySelector('[data-mt-sticky-open]');
    const dialog = sheet?.querySelector('[role="dialog"]');
    let previousFocus = null;

    const closeSheet = () => {
      if (!sheet) return;
      sheet.setAttribute('aria-hidden', 'true');
      sheet.setAttribute('inert', '');
      document.body.style.overflow = '';
      previousFocus?.focus?.();
    };

    const openSheet = () => {
      if (!sheet) return;
      previousFocus = document.activeElement;
      sheet.setAttribute('aria-hidden', 'false');
      sheet.removeAttribute('inert');
      document.body.style.overflow = 'hidden';
      window.requestAnimationFrame(() => dialog?.focus());
    };

    openButton?.addEventListener('click', openSheet);
    sheet?.querySelectorAll('[data-mt-sticky-close]').forEach((button) => button.addEventListener('click', closeSheet));
    document.addEventListener('montrea:cart-added', closeSheet);
    section.addEventListener('keydown', (event) => {
      if (sheet?.getAttribute('aria-hidden') !== 'false') return;
      if (event.key === 'Escape') closeSheet();
      else trapFocus(event, dialog);
    });

    if (!sticky || !mainButton) return;

    let syncFrame = null;
    const syncSticky = () => {
      syncFrame = null;
      sticky.classList.toggle('is-visible', mainButton.getBoundingClientRect().bottom < 0);
    };
    const queueSync = () => {
      if (syncFrame !== null) return;
      syncFrame = window.requestAnimationFrame(syncSticky);
    };

    let observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(queueSync, { threshold: 0 });
      observer.observe(mainButton);
    }
    window.addEventListener('scroll', queueSync, { passive: true });
    window.addEventListener('resize', queueSync);
    syncSticky();
    registerCleanup(section, () => {
      observer?.disconnect();
      window.removeEventListener('scroll', queueSync);
      window.removeEventListener('resize', queueSync);
      document.removeEventListener('montrea:cart-added', closeSheet);
      if (sheet?.getAttribute('aria-hidden') === 'false') closeSheet();
    });
  }

  let cartLastFocus = null;

  function cartShell() {
    return document.querySelector('[data-mt-cart-shell]');
  }

  function openCart(captureFocus = true) {
    const shell = cartShell();
    const drawer = shell?.querySelector('[data-mt-cart-drawer]');
    if (!shell || !drawer) return;
    if (captureFocus) cartLastFocus = document.activeElement;
    shell.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => drawer.querySelector('[data-mt-cart-close]')?.focus());
  }

  function closeCart() {
    const shell = cartShell();
    const drawer = shell?.querySelector('[data-mt-cart-drawer]');
    shell?.classList.remove('is-open');
    drawer?.setAttribute('aria-hidden', 'true');
    drawer?.setAttribute('inert', '');
    document.body.style.overflow = '';
    cartLastFocus?.focus?.();
  }

  async function updateCartCount() {
    try {
      const response = await fetch(`${window.mtRoutes.cart}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-mt-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.classList.toggle('hidden', cart.item_count === 0);
      });
      document.querySelectorAll('[data-mt-cart-open]').forEach((link) => {
        const strings = window.mtStrings || {};
        const cartLabel = strings.cartLabel || 'Warenkorb';
        const itemLabel = cart.item_count === 1 ? (strings.itemSingular || 'Artikel') : (strings.itemPlural || 'Artikel');
        link.setAttribute('aria-label', cart.item_count > 0 ? `${cartLabel}, ${cart.item_count} ${itemLabel}` : cartLabel);
      });
    } catch (_) {
      // Der normale Warenkorb-Link bleibt als Fallback verfügbar.
    }
  }

  async function refreshCart(shouldOpen = true) {
    const response = await fetch(`${window.mtRoutes.cart}?section_id=montrea-cart-drawer`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error(window.mtStrings?.cartUpdateError || 'Warenkorb konnte nicht aktualisiert werden.');
    const html = await response.text();
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    const nextShell = template.content.querySelector('[data-mt-cart-shell]');
    const currentShell = cartShell();
    if (!nextShell || !currentShell) throw new Error(window.mtStrings?.cartViewMissing || 'Warenkorb-Ansicht fehlt.');
    const wasOpen = currentShell.classList.contains('is-open');
    currentShell.replaceWith(nextShell);
    await updateCartCount();
    if (shouldOpen) openCart(!wasOpen);
  }

  async function addProductForm(form) {
    const button = form.querySelector('[data-mt-main-submit]');
    const errorWrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    const errorText = errorWrapper?.querySelector('.product-form__error-message');
    if (button) cartLastFocus = button;
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    button?.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
    if (errorWrapper) errorWrapper.hidden = true;

    try {
      const response = await fetch(`${window.mtRoutes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || window.mtStrings?.addError || 'Das Produkt konnte nicht hinzugefügt werden.');
      await refreshCart(true);
      document.dispatchEvent(new CustomEvent('montrea:cart-added'));
    } catch (error) {
      if (errorText) errorText.textContent = error.message;
      if (errorWrapper) errorWrapper.hidden = false;
    } finally {
      button?.removeAttribute('aria-busy');
      if (button && button.dataset.unavailable !== 'true') button.disabled = false;
      button?.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
    }
  }

  function initCart() {
    if (document.documentElement.dataset.mtCartReady === 'true') return;
    document.documentElement.dataset.mtCartReady = 'true';

    document.addEventListener('click', async (event) => {
      const open = event.target.closest('[data-mt-cart-open]');
      if (open) {
        event.preventDefault();
        openCart();
        return;
      }

      if (event.target.closest('[data-mt-cart-close]')) {
        event.preventDefault();
        closeCart();
        return;
      }

      const change = event.target.closest('[data-mt-cart-change]');
      if (!change) return;
      event.preventDefault();
      change.disabled = true;
      const status = cartShell()?.querySelector('[data-mt-cart-status]');
      try {
        const response = await fetch(`${window.mtRoutes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(change.dataset.line), quantity: Number(change.dataset.quantity) })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || window.mtStrings?.cartUpdateError || 'Menge konnte nicht geändert werden.');
        await refreshCart(true);
      } catch (error) {
        if (status) status.textContent = error.message;
        change.disabled = false;
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-type="add-to-cart-form"]');
      if (!form || !form.closest('[data-mt-main-product]')) return;
      event.preventDefault();
      addProductForm(form);
    });

    document.addEventListener('keydown', (event) => {
      const shell = cartShell();
      if (!shell?.classList.contains('is-open')) return;
      if (event.key === 'Escape') closeCart();
      else trapFocus(event, shell.querySelector('[data-mt-cart-drawer]'));
    });
  }

  function initSection(section) {
    if (!section || section.dataset.mtSectionReady === 'true') return;
    section.dataset.mtSectionReady = 'true';
    initGallery(section);
    initBundles(section);
    initVariants(section);
    initQuiz(section);
    initFeatureTabs(section);
    initFaq(section);
    initBuyAccordions(section);
    initSticky(section);
  }

  function init(root = document) {
    initMenu(root);
    initCart();
    initQuizLinks();
    if (root.matches?.('[data-mt-section], [data-mt-one-product]')) initSection(root);
    root.querySelectorAll?.('[data-mt-section], [data-mt-one-product]').forEach(initSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
})();
